import { FlightDto, FlightModelType, FlightPointDto, LatLonPoint, TvMapLocation, TimePeriod } from 'app/model/CORE';
import { PolygonMeteoForecast } from 'app/model/SOL49';
import * as geom from 'ol/geom';
import OLUtils from 'app/components/map/ol/OLUtils';
import MapConstants from 'app/components/map/MapConstants';
import FlightUtils from 'app/common/util/FlightUtils';
import FlightProfileUtils from 'app/common/util/FlightProfileUtils';
import { MapConvection } from 'app/components/map/CoreMap';
import MapUtils from 'app/components/map/util/MapUtils';
import Utils from 'app/common/util/Utils';
import * as CORE from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import MathUtils from 'app/common/util/MathUtils';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';

import { VolumePrism } from './ContentViewerTypes/VolumePrism';
import { Volume } from './ContentViewerTypes/Volume';
import { PrismRange } from './ContentViewerTypes/PrismRange';
import { Coordinate } from './ContentViewerTypes/Coordinate';
import { Polygon2D } from './ContentViewerTypes/Polygon2D';
import { FlightPlan } from './ContentViewerTypes/FlightPlan';
import { IPathPoint, PathPoint } from './ContentViewerTypes/PathPoint';
import { Aerodrome } from './ContentViewerTypes/Aerodrome';
import { PathPointLoadClassification } from './ContentViewerTypes/PathPointLoadClassification';
import { Convection } from './ContentViewerTypes/Convection';
import { Airspace } from './ContentViewerTypes/Airspace';

export class UnityUtils {
    // NOTE(bjarne): This is unused at the moment but since I'm not sure how we need to create these and
    // we might need to revert some of this, I'll keep it here for now.
    public originalMapTrafficVolumeToVolume(mapTrafficVolume: CORE.MapTrafficVolume) {
        // NOTE(bjarne): This piece of code is partially taken from `OLMap.displayTrafficVolume(...)`.
        const ref: TvMapLocation = mapTrafficVolume.location;
        let polygons: geom.Polygon[] = [];
        // NOTE(bjarne): To have an accurate min and max level, we would need to retrieve them during polygon
        // creation. See getVolumePrismsFromAirspace(...) for an example.
        let maxLevel: number = -Infinity;
        let minLevel: number = Infinity;
        if (ref.airspace) {
            polygons = OLUtils.buildPolygons(ref.airspace);
        } else if (ref.point) {
            polygons.push(OLUtils.buildBox(ref.point.position, MapConstants.BOX_SIZE_OVER_POINT));
            // TODO(bjarne): What do we want to do here?
            maxLevel = 100.0;
            minLevel = 10.0;
        }

        if (mapTrafficVolume.location?.airspace?.composedAirspace) {
            for (const airspace1 of mapTrafficVolume.location?.airspace?.composedAirspace) {
                if (airspace1.airblocks) {
                    maxLevel = Math.max(maxLevel, ...airspace1.airblocks.map(x => x.maxLevel?.value ?? -Infinity));
                    minLevel = Math.min(minLevel, ...airspace1.airblocks.map(x => x.minLevel?.value ?? Infinity));
                }
            }
        }
        if (mapTrafficVolume.location?.airspace?.airblocks) {
            maxLevel = Math.max(
                maxLevel,
                ...mapTrafficVolume.location.airspace.airblocks.map(x => x.maxLevel?.value ?? -Infinity),
            );
            minLevel = Math.min(
                minLevel,
                ...mapTrafficVolume.location.airspace.airblocks.map(x => x.minLevel?.value ?? Infinity),
            );
        }

        const mp: geom.MultiPolygon = new geom.MultiPolygon([]);
        polygons.forEach(x => mp.appendPolygon(x));
        return new Volume({
            prisms: mp.getCoordinates().map(coords => {
                let exteriorPolygon = coords.shift();
                while (exteriorPolygon !== undefined && exteriorPolygon.length === 0) {
                    exteriorPolygon = coords.shift();
                }

                return new VolumePrism({
                    range: new PrismRange({
                        upper: maxLevel,
                        lower: minLevel,
                    }),
                    polygon: new Polygon2D({
                        exterior:
                            exteriorPolygon !== undefined
                                ? exteriorPolygon.map(y => {
                                      return new Coordinate({ longitude: y[0], latitude: y[1] });
                                  })
                                : [],
                        interior: coords.map(y =>
                            y.map(z => {
                                return new Coordinate({ longitude: z[0], latitude: z[1] });
                            }),
                        ),
                    }),
                });
            }),
        });
    }

    private getCoordinate(point: LatLonPoint) {
        return new Coordinate({
            latitude: point.latitude,
            longitude: point.longitude,
        });
    }

    private getVolumePrismsFromAirspace(airspace: CORE.Airspace) {
        // NOTE(bjarne): Adapted from OLUtils.ts:buildPolygons.
        let prisms: VolumePrism[] = [];
        for (const airspace1 of airspace.composedAirspace) {
            // TODO(bjarne): Should these be treated as separate volumes entirely or just separate prisms?
            prisms = prisms.concat(this.getVolumePrismsFromAirspace(airspace1));
        }
        for (const ab3 of airspace.airblocks) {
            const airblock = ab3.airblock;
            if (airblock && airblock.points) {
                const airblockCoordinates = airblock.points.map(abPt => this.getCoordinate(abPt));
                prisms.push(
                    new VolumePrism({
                        range: new PrismRange({
                            upper: ab3.maxLevel.value,
                            lower: ab3.minLevel.value,
                        }),
                        // TODO(bjarne): Doing these polygons one by one ensures that we get the correct altitude, BUT it
                        // also prevents us from creating interior polygons. If I knew what the input data
                        // actually looks like I could make a good decision, but I'm flying in the dark here.
                        polygon: new Polygon2D({
                            exterior: airblockCoordinates,
                            interior: [],
                        }),
                    }),
                );
            }
        }

        return prisms;
    }

    public getVolumeFromAirspace(airspace: CORE.Airspace) {
        return new Volume({
            prisms: this.getVolumePrismsFromAirspace(airspace),
        });
    }

    public getVolumeFromMapTrafficVolume(mapTrafficVolume: CORE.MapTrafficVolume) {
        if (mapTrafficVolume.location?.airspace) {
            return this.getVolumeFromAirspace(mapTrafficVolume.location.airspace);
        }

        if (mapTrafficVolume.location?.point) {
            const polygon = OLUtils.buildBox(
                mapTrafficVolume.location.point.position,
                MapConstants.BOX_SIZE_OVER_POINT,
            );
            const maxLevel = FlightLevelUtils.UNLIMITED_VALUE;
            const minLevel = FlightLevelUtils.ON_GROUND_VALUE;

            return new Volume({
                prisms: polygon.getCoordinates().map(coords => {
                    return new VolumePrism({
                        range: new PrismRange({
                            upper: maxLevel,
                            lower: minLevel,
                        }),
                        polygon: new Polygon2D({
                            exterior: coords.map(y => {
                                return new Coordinate({ longitude: y[0], latitude: y[1] });
                            }),
                            interior: [],
                        }),
                    });
                }),
            });
        }

        return new Volume();
    }

    public getAerodrome(pointDto: FlightPointDto) {
        return new Aerodrome({
            id: FlightProfileUtils.getName(pointDto),
            name: FlightProfileUtils.getDisplayString(pointDto),
            location: this.getCoordinate(pointDto.point.position),
        });
    }

    private intersectSegment(flightPoints: FlightPointDto[], flightPointIndex: number, date: Date): FlightPointDto {
        const flightPoint = flightPoints[flightPointIndex];

        if (!TimeUtils.isEquals(flightPoint.timeOver, date)) {
            const prevPointWithPos = flightPoints
                .slice(0, flightPointIndex)
                .reverse()
                .find(x => Utils.isExists(x.point?.position));
            const nextPointWithPos = flightPoints
                .slice(flightPointIndex + 1, flightPoints.length)
                .find(x => Utils.isExists(x.point?.position));
            if (prevPointWithPos && nextPointWithPos) {
                const percentage = MathUtils.calcPercentageAlong(
                    prevPointWithPos.timeOver.getTime(),
                    nextPointWithPos.timeOver.getTime(),
                    date.getTime(),
                );
                return {
                    timeOver: new Date(
                        MathUtils.interpolate(
                            prevPointWithPos.timeOver.getTime(),
                            nextPointWithPos.timeOver.getTime(),
                            percentage,
                        ),
                    ),
                    flightLevel: {
                        unit: CORE.FlightLevel_Unit.FLIGHT_LEVEL,
                        value: MathUtils.interpolate(
                            FlightLevelUtils.getLevelValue(prevPointWithPos.flightLevel),
                            FlightLevelUtils.getLevelValue(nextPointWithPos.flightLevel),
                            percentage,
                        ),
                    },
                    point: {
                        position: {
                            latitude: MathUtils.interpolate(
                                prevPointWithPos.point.position.latitude,
                                nextPointWithPos.point.position.latitude,
                                percentage,
                            ),
                            longitude: MathUtils.interpolate(
                                prevPointWithPos.point.position.longitude,
                                nextPointWithPos.point.position.longitude,
                                percentage,
                            ),
                        },
                    },
                };
            } else {
                console.warn('Cannot interpolate split point', prevPointWithPos, nextPointWithPos);
            }
        } else if (!flightPoint.point?.position) {
            // NOTE(bjarne): We intersected the segment at exactly our time, but we didn't have positional data.
            const prevPointWithPos = flightPoints
                .slice(0, flightPointIndex)
                .reverse()
                .find(x => Utils.isExists(x.point?.position));
            const nextPointWithPos = flightPoints
                .slice(flightPointIndex + 1, flightPoints.length)
                .find(x => Utils.isExists(x.point?.position));
            if (prevPointWithPos && nextPointWithPos) {
                const percentage = MathUtils.calcPercentageAlong(
                    prevPointWithPos.timeOver.getTime(),
                    nextPointWithPos.timeOver.getTime(),
                    date.getTime(),
                );
                flightPoint.flightLevel = {
                    unit: CORE.FlightLevel_Unit.FLIGHT_LEVEL,
                    value: MathUtils.interpolate(
                        FlightLevelUtils.getLevelValue(prevPointWithPos.flightLevel),
                        FlightLevelUtils.getLevelValue(nextPointWithPos.flightLevel),
                        percentage,
                    ),
                };
                flightPoint.point = {
                    position: {
                        latitude: MathUtils.interpolate(
                            prevPointWithPos.point.position.latitude,
                            nextPointWithPos.point.position.latitude,
                            percentage,
                        ),
                        longitude: MathUtils.interpolate(
                            prevPointWithPos.point.position.longitude,
                            nextPointWithPos.point.position.longitude,
                            percentage,
                        ),
                    },
                };
            } else {
                console.warn('Cannot interpolate split point', prevPointWithPos, nextPointWithPos);
            }
        }

        return flightPoint;
    }

    private coincide(a: IPathPoint, b: IPathPoint) {
        return a.location.latitude === b.location.latitude && a.location.longitude === b.location.longitude;
    }

    public getFlightPlan(
        flight: FlightDto,
        model: FlightModelType,
        segmentHighlights: ProfileSegmentHighlight[],
    ): FlightPlan {
        const flightPoints = FlightUtils.getPoints(flight, model);
        if (flightPoints.length == 0) {
            return new FlightPlan({ airspaces: [], path: [] });
        }

        const resultPoints: PathPoint[] = [];
        let prevPoint: FlightPointDto;
        const segments = segmentHighlights.slice();
        let currentSegment = segments.shift();
        let loadClassification: PathPointLoadClassification | undefined = undefined;
        for (let i = 0; i < flightPoints.length; ++i) {
            const flightPoint = flightPoints[i];
            if (
                currentSegment &&
                !prevPoint &&
                TimePeriodUtils.containsInc(currentSegment.timePeriod, flightPoint.timeOver)
            ) {
                // NOTE(bjarne): Our first point lies within a segment.
                loadClassification = currentSegment.loadClassification;
            }

            while (currentSegment && prevPoint) {
                const crossesStart = TimePeriodUtils.containsInc(
                    { from: prevPoint.timeOver, to: flightPoint.timeOver },
                    currentSegment.timePeriod.from,
                );
                const crossesEnd = TimePeriodUtils.containsInc(
                    { from: prevPoint.timeOver, to: flightPoint.timeOver },
                    currentSegment.timePeriod.to,
                );

                if (!crossesStart && !crossesEnd) {
                    break;
                }

                // NOTE(bjarne): The line between the previous point and this one crosses the segment boundary.
                if (crossesStart) {
                    const intersection = this.intersectSegment(flightPoints, i, currentSegment.timePeriod.from);
                    const newPointData: IPathPoint = {
                        altitudePath: intersection.flightLevel?.value,
                        location: this.getCoordinate(intersection.point.position),
                        loadClassification: loadClassification,
                    };

                    // NOTE(bjarne): We add another point on the same location to facilitate easy segmentation
                    // TODO(bjarne): Do we want this?
                    resultPoints.push(new PathPoint(newPointData));

                    loadClassification = currentSegment.loadClassification;
                    newPointData.loadClassification = loadClassification;
                    if (intersection !== flightPoint) {
                        resultPoints.push(new PathPoint(newPointData));
                    }
                }

                if (crossesEnd) {
                    const intersection = this.intersectSegment(flightPoints, i, currentSegment.timePeriod.to);
                    const newPointData: IPathPoint = {
                        altitudePath: intersection.flightLevel?.value,
                        location: this.getCoordinate(intersection.point.position),
                        loadClassification: loadClassification,
                    };

                    // NOTE(bjarne): We add another point on the same location to facilitate easy segmentation
                    resultPoints.push(new PathPoint(newPointData));

                    loadClassification = undefined;
                    newPointData.loadClassification = loadClassification;
                    if (intersection !== flightPoint) {
                        resultPoints.push(new PathPoint(newPointData));
                    }
                }

                if (crossesEnd) {
                    currentSegment = segments.shift();
                } else {
                    // NOTE(bjarne): We crossed the start of this segment, but the end lies beyond this point.
                    break;
                }
            }

            resultPoints.push(
                new PathPoint({
                    altitudePath: flightPoint.flightLevel?.value,
                    location: this.getCoordinate(
                        FlightProfileUtils.isVectorPoint(flightPoint)
                            ? FlightProfileUtils.getVectorPointPosition(flightPoints, i)
                            : flightPoint.point.position,
                    ),
                    loadClassification: loadClassification,
                }),
            );
            prevPoint = flightPoint;
        }

        // NOTE(bjarne): Removing redundant points.
        let segmentPointCount = 1;
        for (let i = 1; i < resultPoints.length; ++i) {
            const thisPoint = resultPoints[i];
            const previousPoint = resultPoints[i - 1];
            if (
                this.coincide(previousPoint, thisPoint) &&
                thisPoint.loadClassification === previousPoint.loadClassification
            ) {
                // NOTE(bjarne): This point is redundant.
                resultPoints.splice(i, 1);
                --i;
            } else if (thisPoint.loadClassification !== previousPoint.loadClassification) {
                if (segmentPointCount <= 1) {
                    // NOTE(bjarne): There was only one point in the previous segment (might happen when crossing adjacent
                    // segments and creating a default point between them).
                    resultPoints.splice(i - 1, 1);
                    --i;
                }
                segmentPointCount = 1;
            } else {
                ++segmentPointCount;
            }
        }

        if (segmentPointCount <= 1) {
            // NOTE(bjarne): There was only one point in the last segment (might happen when crossing adjacent
            // segments and creating a default point between them).
            resultPoints.pop();
        }

        return new FlightPlan({
            airspaces: FlightUtils.getAirspacePoints(flight, model).map(x => x.airspaceId),
            path: resultPoints,
        });
    }

    private getVolumeFromPolygon(
        polygon: LatLonPoint[],
        upperAltitude: CORE.FlightLevel,
        lowerAltitude: CORE.FlightLevel,
    ) {
        return new Volume({
            prisms: [
                new VolumePrism({
                    range: new PrismRange({
                        upper: upperAltitude.value,
                        lower: lowerAltitude.value,
                    }),
                    polygon: new Polygon2D({
                        exterior: polygon.map(x => this.getCoordinate(x)),
                        interior: [],
                    }),
                }),
            ],
        });
    }

    public getPolygonConvections(
        polygonConvection: MapConvection[],
        showTVs: boolean,
        hideConvectionWithNoImpact: boolean,
        timePeriod?: TimePeriod,
    ): Convection[] {
        // NOTE(bjarne): This is adapted from OLMap.displayPolygonConvection
        const parsedConvections = polygonConvection
            .filter(
                mac =>
                    (!timePeriod ||
                        MapUtils.shouldDisplayPolygonConvection(mac.convection.validityPeriod, timePeriod)) &&
                    (!hideConvectionWithNoImpact ||
                        (Utils.isExists(mac.tvAirspaceMap) && Utils.isNotEmpty(Object.keys(mac.tvAirspaceMap)))),
            )
            .map(mac => {
                const convection = mac.convection as PolygonMeteoForecast;
                let trafficVolumes: Airspace[] = [];
                let weatherVolumes: Volume[] = [];
                if (showTVs) {
                    trafficVolumes = Object.entries(mac.tvAirspaceMap).map(([tv, airspace]) => {
                        return new Airspace({
                            id: tv,
                            volumes: [this.getVolumeFromAirspace(airspace)],
                        });
                    });
                } else {
                    weatherVolumes = [
                        this.getVolumeFromPolygon(
                            convection.polygon,
                            convection.flightLevelBand?.min,
                            convection.flightLevelBand?.max,
                        ),
                    ];
                }

                return new Convection({
                    id: convection.id,
                    level: convection.level,
                    likeliness: convection.likeliness,
                    convectionFraction: convection.convectionFraction,
                    isInvalid: !TimePeriodUtils.intersects(convection.validityPeriod, TimeUtils.NOW()),
                    trafficVolumes: trafficVolumes,
                    weatherVolumes: weatherVolumes,
                });
            });

        return parsedConvections;
    }

    public getTvConvections(convections: MapConvection[]): Convection[] {
        // NOTE(bjarne): This is adapted from OLMap.displayTvConvection.
        const parsedConvections = convections.map(mac => {
            const convection = mac.convection;
            const [tv, airspace] = Object.entries(mac.tvAirspaceMap)[0];
            return new Convection({
                id: convection.id,
                level: convection.level,
                isInvalid: false,
                trafficVolumes: [
                    new Airspace({
                        id: tv,
                        volumes: [this.getVolumeFromAirspace(airspace)],
                    }),
                ],
                weatherVolumes: [],

                likeliness: undefined,
                convectionFraction: undefined,
            });
        });

        return parsedConvections;
    }
}
